import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import usePageTitle from '../../hooks/usePageTitle';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faStar,
  faBookmark,
  faDollarSign,
  faShareNodes,
  faArrowLeft,
  faUser,
  faCircleExclamation,
  faGlobe,
  faMobileScreenButton,
  faDesktop,
  faTabletScreenButton,
  faHashtag,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faInstagram,
  faXTwitter,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";
import DashboardShell from "../../components/Dashboard/DashboardShell";
import LoadingSpin from "../../components/Dashboard/LoadingSpin";
import AnalyticsDataService from "../../api/analytics";

const STATUS_STYLES = {
  pending:  "tw-bg-yellow-500/20 tw-text-yellow-300",
  approved: "tw-bg-green-500/20 tw-text-green-300",
  refused:  "tw-bg-red-500/20 tw-text-red-300",
};

function StatCard({ icon, label, value, color }) {
  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-p-4">
      <div className={`tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full ${color}`}>
        <FontAwesomeIcon icon={icon} className="tw-text-white tw-text-base" />
      </div>
      <span className="tw-text-2xl tw-font-bold tw-text-white">{value}</span>
      <span className="tw-text-xs tw-text-white/50 tw-text-center">{label}</span>
    </div>
  );
}

function UserCard({ user: u }) {
  const avatarUrl =
    u?.picture && !u.picture.startsWith("http")
      ? `${process.env.REACT_APP_AWS_URL}/${u.picture}`
      : u?.picture;

  return (
    <div className="tw-flex tw-items-center tw-gap-2.5 tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-px-3 tw-py-2 tw-w-fit tw-max-w-[200px]">
      {avatarUrl ? (
        <img
          src={avatarUrl}
          alt={u.firstName}
          className="tw-h-8 tw-w-8 tw-rounded-full tw-object-cover tw-shrink-0"
        />
      ) : (
        <div className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/10 tw-shrink-0">
          <FontAwesomeIcon icon={faUser} className="tw-text-white/40 tw-text-xs" />
        </div>
      )}
      <div className="tw-min-w-0">
        <p className="tw-text-xs tw-font-medium tw-text-white tw-truncate tw-m-0">
          {u?.firstName} {u?.lastName}
        </p>
        <p className="tw-text-xs tw-text-white/40 tw-m-0 tw-truncate">{u?.role}</p>
      </div>
    </div>
  );
}

function EngagementPanel({ engagement, t }) {
  const TABS = [
    { key: "likes",      label: t("Liked"),        icon: faStar,        users: engagement.likes },
    { key: "favourites", label: t("Saved"),         icon: faBookmark,    users: engagement.favourites },
    { key: "wishesToBuy",label: t("Want to Buy"),   icon: faDollarSign,  users: engagement.wishesToBuy },
  ];

  const [active, setActive] = useState("likes");
  const current = TABS.find(tab => tab.key === active);

  return (
    <div className="tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-p-4 tw-flex tw-flex-col tw-gap-4">
      <h2 className="tw-text-white tw-font-semibold tw-text-base tw-m-0">{t("Who Engaged")}</h2>

      <div className="tw-flex tw-gap-2 tw-flex-wrap">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActive(tab.key)}
            className={`tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-border-none tw-transition-colors tw-cursor-pointer
              ${active === tab.key
                ? "tw-bg-[#712CB0] tw-text-white"
                : "tw-bg-white/5 tw-text-white/60 hover:tw-bg-white/10"
              }`}
          >
            <FontAwesomeIcon icon={tab.icon} className="tw-text-xs" />
            {tab.label}
            <span className="tw-rounded-full tw-bg-black/10 tw-px-1.5 tw-py-0.5 tw-text-xs tw-leading-none">
              {tab.users?.length ?? 0}
            </span>
          </button>
        ))}
      </div>

      {current?.users?.length > 0 ? (
        <div className="tw-flex tw-flex-wrap tw-gap-2">
          {current.users.map((u) => (
            <UserCard key={u._id} user={u} />
          ))}
        </div>
      ) : (
        <p className="tw-text-white/40 tw-text-sm tw-text-center tw-py-6 tw-m-0">
          {t("No engagement yet.")}
        </p>
      )}
    </div>
  );
}

function AccessRequestsPanel({ accessRequests, t }) {
  const [filter, setFilter] = useState("all");

  const FILTERS = [
    { key: "all",      label: t("All") },
    { key: "pending",  label: t("Pending") },
    { key: "approved", label: t("Approved") },
    { key: "refused",  label: t("Refused") },
  ];

  const filtered =
    filter === "all"
      ? accessRequests
      : accessRequests.filter((r) => r.status === filter);

  return (
    <div className="tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-p-4 tw-flex tw-flex-col tw-gap-4">
      <h2 className="tw-text-white tw-font-semibold tw-text-base tw-m-0">{t("Access Requests")}</h2>

      <div className="tw-flex tw-gap-2 tw-flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`tw-rounded-lg tw-px-3 tw-py-1.5 tw-text-sm tw-border-none tw-transition-colors tw-cursor-pointer
              ${filter === f.key
                ? "tw-bg-[#712CB0] tw-text-white"
                : "tw-bg-white/5 tw-text-white/60 hover:tw-bg-white/10"
              }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="tw-text-white/40 tw-text-sm tw-text-center tw-py-6 tw-m-0">
          {t("No access requests yet.")}
        </p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="tw-hidden md:tw-block tw-overflow-x-auto">
            <table className="tw-w-full tw-text-sm">
              <thead>
                <tr className="tw-border-b tw-border-white/10">
                  <th className="tw-text-left tw-text-white/40 tw-font-medium tw-py-2 tw-pr-4">{t("User")}</th>
                  <th className="tw-text-left tw-text-white/40 tw-font-medium tw-py-2 tw-pr-4">{t("Role")}</th>
                  <th className="tw-text-left tw-text-white/40 tw-font-medium tw-py-2 tw-pr-4">{t("Message")}</th>
                  <th className="tw-text-left tw-text-white/40 tw-font-medium tw-py-2 tw-pr-4">{t("Date")}</th>
                  <th className="tw-text-left tw-text-white/40 tw-font-medium tw-py-2">{t("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((req, i) => (
                  <tr key={i} className="tw-border-b tw-border-white/5 last:tw-border-0">
                    <td className="tw-py-3 tw-pr-4">
                      <div className="tw-flex tw-items-center tw-gap-2">
                        {req.user?.picture ? (
                          <img
                            src={
                              req.user.picture.startsWith("http")
                                ? req.user.picture
                                : `${process.env.REACT_APP_AWS_URL}/${req.user.picture}`
                            }
                            alt=""
                            className="tw-h-7 tw-w-7 tw-rounded-full tw-object-cover tw-shrink-0"
                          />
                        ) : (
                          <div className="tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/10 tw-shrink-0">
                            <FontAwesomeIcon icon={faUser} className="tw-text-white/30 tw-text-xs" />
                          </div>
                        )}
                        <span className="tw-text-white/80">
                          {req.user ? `${req.user.firstName} ${req.user.lastName}` : t("Unknown")}
                        </span>
                      </div>
                    </td>
                    <td className="tw-py-3 tw-pr-4 tw-text-white/50">{req.user?.role ?? "--"}</td>
                    <td className="tw-py-3 tw-pr-4 tw-text-white/60 tw-max-w-[200px] tw-truncate">{req.comment || "--"}</td>
                    <td className="tw-py-3 tw-pr-4 tw-text-white/40 tw-whitespace-nowrap">
                      {req.createdAt ? new Date(req.createdAt).toLocaleDateString() : "--"}
                    </td>
                    <td className="tw-py-3">
                      <span className={`tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium ${STATUS_STYLES[req.status] ?? ""}`}>
                        {t(req.status)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="tw-flex tw-flex-col tw-gap-2 md:tw-hidden">
            {filtered.map((req, i) => (
              <div key={i} className="tw-rounded-lg tw-bg-white/5 tw-p-3 tw-flex tw-flex-col tw-gap-1.5">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <span className="tw-text-white/80 tw-text-sm tw-font-medium">
                    {req.user ? `${req.user.firstName} ${req.user.lastName}` : t("Unknown")}
                  </span>
                  <span className={`tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium ${STATUS_STYLES[req.status] ?? ""}`}>
                    {t(req.status)}
                  </span>
                </div>
                <div className="tw-flex tw-gap-3 tw-text-xs tw-text-white/40">
                  <span>{req.user?.role}</span>
                  <span>·</span>
                  <span>{req.createdAt ? new Date(req.createdAt).toLocaleDateString() : "--"}</span>
                </div>
                {req.comment && (
                  <p className="tw-text-xs tw-text-white/50 tw-truncate tw-m-0">{req.comment}</p>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function formatReach(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1_000)     return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(n);
}

const PLATFORM_META = [
  { key: 'facebook',  icon: faFacebook,  label: 'Facebook',  color: 'tw-text-blue-400' },
  { key: 'instagram', icon: faInstagram, label: 'Instagram', color: 'tw-text-pink-400' },
  { key: 'twitter',   icon: faXTwitter,  label: 'X / Twitter', color: 'tw-text-sky-400' },
  { key: 'tiktok',    icon: faTiktok,    label: 'TikTok',    color: 'tw-text-white' },
];

function CrewReachPanel({ crewReach, t }) {
  if (!crewReach || crewReach.total === 0) {
    return (
      <div className="tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-p-4 tw-flex tw-flex-col tw-gap-3">
        <h2 className="tw-text-white tw-font-semibold tw-text-base tw-m-0">{t("Crew Social Reach")}</h2>
        <p className="tw-text-white/40 tw-text-sm tw-py-4 tw-text-center tw-m-0">{t("No crew social data yet.")}</p>
      </div>
    );
  }

  const max = Math.max(crewReach.facebook, crewReach.instagram, crewReach.twitter, crewReach.tiktok, 1);

  return (
    <div className="tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-p-4 tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-items-baseline tw-justify-between">
        <h2 className="tw-text-white tw-font-semibold tw-text-base tw-m-0">{t("Crew Social Reach")}</h2>
        <div className="tw-flex tw-items-baseline tw-gap-1.5">
          <FontAwesomeIcon icon={faHashtag} className="tw-text-[#712CB0] tw-text-sm" />
          <span className="tw-text-xl tw-font-bold tw-text-white">{formatReach(crewReach.total)}</span>
          <span className="tw-text-xs tw-text-white/40">{t("total reach")}</span>
        </div>
      </div>

      <div className="tw-flex tw-flex-col tw-gap-3">
        {PLATFORM_META.map(({ key, icon, label, color }) => {
          const val = crewReach[key] || 0;
          const pct = Math.round((val / max) * 100);
          return (
            <div key={key} className="tw-flex tw-items-center tw-gap-3">
              <FontAwesomeIcon icon={icon} className={`tw-text-sm tw-w-4 tw-shrink-0 ${color}`} />
              <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-1">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <span className="tw-text-xs tw-text-white/50">{label}</span>
                  <span className="tw-text-xs tw-font-semibold tw-text-white/70">{formatReach(val)}</span>
                </div>
                <div className="tw-h-1.5 tw-rounded-full tw-bg-white/10 tw-overflow-hidden">
                  <div
                    className="tw-h-full tw-rounded-full tw-bg-[#712CB0] tw-transition-all"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const DEVICE_META = {
  desktop: { icon: faDesktop,             label: 'Desktop', color: 'tw-text-sky-400' },
  mobile:  { icon: faMobileScreenButton,  label: 'Mobile',  color: 'tw-text-green-400' },
  tablet:  { icon: faTabletScreenButton,  label: 'Tablet',  color: 'tw-text-amber-400' },
};

function GeographyPanel({ geo, t }) {
  const countries = geo?.countries || [];
  const devices   = geo?.devices   || [];

  const totalViews = countries.reduce((s, c) => s + c.count, 0);
  const totalDev   = devices.reduce((s, d) => s + d.count, 0);

  const nameOf = (code) => {
    try { return new Intl.DisplayNames(['en'], { type: 'region' }).of(code); } catch { return code; }
  };

  const hasData = totalViews > 0;

  return (
    <div className="tw-rounded-xl tw-bg-[#280D41] tw-border tw-border-white/10 tw-p-4 tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-items-center tw-gap-2">
        <FontAwesomeIcon icon={faGlobe} className="tw-text-[#712CB0]" />
        <h2 className="tw-text-white tw-font-semibold tw-text-base tw-m-0">{t("Viewer Geography")}</h2>
      </div>

      {!hasData ? (
        <p className="tw-text-white/40 tw-text-sm tw-py-4 tw-text-center tw-m-0">{t("No geography data yet.")}</p>
      ) : (
        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-6">
          {/* Countries */}
          <div className="tw-flex tw-flex-col tw-gap-2.5">
            <p className="tw-text-xs tw-text-white/40 tw-font-medium tw-uppercase tw-tracking-wide tw-m-0">{t("Top Countries")}</p>
            {countries.map(({ code, count }) => {
              const pct = Math.round((count / totalViews) * 100);
              return (
                <div key={code} className="tw-flex tw-items-center tw-gap-2">
                  <span className="tw-text-sm tw-w-6 tw-text-center">{code === 'XX' ? '?' : countryFlag(code)}</span>
                  <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-0.5">
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <span className="tw-text-xs tw-text-white/70">{code === 'XX' ? t("Unknown") : nameOf(code)}</span>
                      <span className="tw-text-xs tw-text-white/40">{pct}%</span>
                    </div>
                    <div className="tw-h-1 tw-rounded-full tw-bg-white/10 tw-overflow-hidden">
                      <div className="tw-h-full tw-rounded-full tw-bg-[#712CB0]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Devices */}
          <div className="tw-flex tw-flex-col tw-gap-2.5">
            <p className="tw-text-xs tw-text-white/40 tw-font-medium tw-uppercase tw-tracking-wide tw-m-0">{t("Devices")}</p>
            {devices.map(({ type, count }) => {
              const meta = DEVICE_META[type] || DEVICE_META.desktop;
              const pct = Math.round((count / totalDev) * 100);
              return (
                <div key={type} className="tw-flex tw-items-center tw-gap-3">
                  <FontAwesomeIcon icon={meta.icon} className={`tw-text-sm tw-w-4 tw-shrink-0 ${meta.color}`} />
                  <div className="tw-flex-1 tw-flex tw-flex-col tw-gap-0.5">
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <span className="tw-text-xs tw-text-white/70">{t(meta.label)}</span>
                      <span className="tw-text-xs tw-text-white/40">{pct}%</span>
                    </div>
                    <div className="tw-h-1 tw-rounded-full tw-bg-white/10 tw-overflow-hidden">
                      <div className="tw-h-full tw-rounded-full tw-bg-[#712CB0]" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function countryFlag(code) {
  return code.toUpperCase().replace(/./g, c =>
    String.fromCodePoint(0x1F1E6 - 65 + c.charCodeAt(0))
  );
}

export default function EpkAnalyticsPage() {
  usePageTitle('EPK Analytics');
  const { t } = useTranslation();
  const { id } = useParams();
  const user = useSelector((state) => state.user);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    AnalyticsDataService.getEpkAnalytics(id, user.token)
      .then((res) => { setData(res.data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [id, user.token]);

  const STATS = data
    ? [
        { icon: faEye,        label: t("Views"),        value: data.stats.views,       color: "tw-bg-blue-500/30" },
        { icon: faStar,       label: t("Likes"),         value: data.stats.likes,       color: "tw-bg-yellow-500/30" },
        { icon: faBookmark,   label: t("Saved"),         value: data.stats.favourites,  color: "tw-bg-purple-500/30" },
        { icon: faDollarSign, label: t("Buy Interest"),  value: data.stats.wishesToBuy, color: "tw-bg-green-500/30" },
        { icon: faShareNodes, label: t("Shares"),        value: data.stats.shares,      color: "tw-bg-pink-500/30" },
      ]
    : [];

  return (
    <DashboardShell role={user.role} pageTitle={t("EPK Analytics")}>
      <div className="tw-flex tw-flex-col tw-gap-6">

        <div className="tw-flex tw-items-center tw-gap-3">
          <Link
            to="/dashboard/analytics"
            className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-white/60 hover:tw-text-white tw-no-underline tw-transition-colors"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="tw-text-xs" />
            {t("Back to Analytics")}
          </Link>
          {data && (
            <span className="tw-text-white/20">·</span>
          )}
          {data && (
            <span className="tw-text-sm tw-text-white/60 tw-truncate">{data.title}</span>
          )}
        </div>

        {loading && <LoadingSpin />}

        {error && !loading && (
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-16 tw-text-center">
            <FontAwesomeIcon icon={faCircleExclamation} className="tw-text-red-400 tw-text-2xl" />
            <p className="tw-text-white/50 tw-text-sm tw-m-0">{t("Failed to load analytics.")}</p>
          </div>
        )}

        {data && !loading && (
          <>
            <div className="tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 md:tw-grid-cols-5 tw-gap-3">
              {STATS.map((s) => (
                <StatCard key={s.label} {...s} />
              ))}
            </div>

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <CrewReachPanel crewReach={data.crewReach} t={t} />
              <GeographyPanel geo={data.geo} t={t} />
            </div>
            <EngagementPanel engagement={data.engagement} t={t} />
            <AccessRequestsPanel accessRequests={data.accessRequests} t={t} />
          </>
        )}
      </div>
    </DashboardShell>
  );
}
